import { useEffect, useState } from "react";
import type { Alignment, DistributionAxis } from "./canvas-commands.js";
import { canvasCopy } from "../../copy/zh-CN.js";

export interface SelectionToolbarProps {
  selectedCount: number;
  selectedCollapsedCount: number;
  bulkMediaCount: number;
  bulkSelectionMode: boolean;
  seedanceTargets: readonly { id: string; label: string }[];
  onCreateBulkSeedance(): void;
  onConnectBulkToSeedance(targetNodeId: string): void;
  onAlign(alignment: Alignment): void;
  onDistribute(axis: DistributionAxis): void;
  onGroup(): void;
  onUngroup(): void;
  onFold(): void;
  onDuplicate(): void;
  onDelete(): void;
}

export function SelectionToolbar(props: SelectionToolbarProps) {
  const [targetId, setTargetId] = useState("");
  useEffect(() => {
    setTargetId((current) => props.seedanceTargets.some((target) => target.id === current)
      ? current
      : (props.seedanceTargets[0]?.id ?? ""));
  }, [props.seedanceTargets]);
  if (props.selectedCount === 0 && !props.bulkSelectionMode) return null;
  const disabled = props.selectedCount < 2;
  const canCreateBulkSeedance = props.bulkMediaCount >= 2;
  return (
    <aside className="studio-selection-toolbar" aria-label={canvasCopy.selectedNodeActions}>
      {props.bulkSelectionMode && !canCreateBulkSeedance ? <span className="studio-selection-toolbar__bulk-hint">{canvasCopy.bulkSelectedMedia} {props.bulkMediaCount} 个，{canvasCopy.bulkSelectionHint}</span> : null}
      {canCreateBulkSeedance ? <>
        <span className="studio-selection-toolbar__bulk-hint">{canvasCopy.bulkSelectedMedia} {props.bulkMediaCount} 个，{canvasCopy.bulkDragHint}</span>
        <button type="button" onClick={props.onCreateBulkSeedance}>{canvasCopy.createBulkSeedance}</button>
        {props.seedanceTargets.length > 0 ? <>
          <select aria-label={canvasCopy.selectBulkSeedanceTarget} value={targetId} onChange={(event) => setTargetId(event.currentTarget.value)}>
            {props.seedanceTargets.map((target) => <option key={target.id} value={target.id}>{target.label}</option>)}
          </select>
          <button type="button" onClick={() => props.onConnectBulkToSeedance(targetId)}>{canvasCopy.connectBulkToSeedance}</button>
        </> : null}
      </> : null}
      {props.selectedCount > 0 ? <>
        <span>{canvasCopy.selectedNodeCount}：{props.selectedCount}</span>
        <button type="button" aria-label={canvasCopy.alignLeft} onClick={() => props.onAlign("left")} disabled={disabled}>{canvasCopy.alignLeft}</button>
        <button type="button" aria-label={canvasCopy.alignTop} onClick={() => props.onAlign("top")} disabled={disabled}>{canvasCopy.alignTop}</button>
        <button type="button" aria-label={canvasCopy.distributeHorizontal} onClick={() => props.onDistribute("horizontal")} disabled={props.selectedCount < 3}>{canvasCopy.distributeHorizontal}</button>
        <button type="button" aria-label={canvasCopy.distributeVertical} onClick={() => props.onDistribute("vertical")} disabled={props.selectedCount < 3}>{canvasCopy.distributeVertical}</button>
        <button type="button" aria-label={canvasCopy.group} onClick={props.onGroup}>{canvasCopy.group}</button>
        <button type="button" aria-label={canvasCopy.ungroup} onClick={props.onUngroup}>{canvasCopy.ungroup}</button>
        <button type="button" aria-label={props.selectedCollapsedCount === props.selectedCount ? canvasCopy.unfold : canvasCopy.fold} onClick={props.onFold}>{props.selectedCollapsedCount === props.selectedCount ? canvasCopy.unfold : canvasCopy.fold}</button>
        <button type="button" aria-label={canvasCopy.duplicate} onClick={props.onDuplicate}>{canvasCopy.duplicate}</button>
        <button type="button" aria-label="删除所选节点" className="studio-selection-toolbar__delete" onClick={props.onDelete}>{canvasCopy.delete}</button>
      </> : null}
    </aside>
  );
}
